import { useTheme } from '@ad-lucy/ui';
import {
  ANIMATION_DURATION,
  ANIMATION_TIMING_FUNCTION,
  animateNavigationMenu,
  getNavigationMenuHeight,
  getRenderSidebarCollapsed,
} from '@modules/Layout/Default/animationCore';
import { useLayoutContext } from '@modules/Layout/LayoutContext';
// TS breaks type inference if we destructure a discriminated union
import styled, { css, type keyframes } from 'styled-components';

type Keyframes = ReturnType<typeof keyframes>;

type NavigationMenuListProps = {
  nested: boolean;
  collapsed: boolean;
  theme: object;
  $animation?: Keyframes;
};

const navigationMenuListAnimation = (props: NavigationMenuListProps) =>
  css<NavigationMenuListProps>`${props.$animation} forwards ${ANIMATION_DURATION}ms ${ANIMATION_TIMING_FUNCTION};`;

// Warning: changing the final height of this element directly affects the sidebar animation
// e.g. changing the top or bottom padding/margin values
const NavigationMenuList = styled.ul<NavigationMenuListProps>`
  padding-left: calc(${({ nested, collapsed, theme }) =>
    !collapsed && nested && `${theme?.spacing?.lg}*2`});
  display: flex;
  flex-direction: column;
  align-self: ${({ collapsed, nested }) => collapsed && !nested && 'center'};
  align-items: ${({ collapsed, nested }) => collapsed && !nested && 'center'};
  animation: ${navigationMenuListAnimation};
  overflow: hidden;
`;

type NavigationMenuProps = React.PropsWithChildren<
  | {
      nested?: false;
    }
  | {
      itemCount: number;
      nested: true;
    }
>;

// Warning: changing the final height of this component directly affects the sidebar animation
// e.g. changing the children components that are rendered
export default function NavBarMenu(props: Readonly<NavigationMenuProps>) {
  const { theme } = useTheme();
  const { sidebarCollapsed, sidebarTransitionEnded } = useLayoutContext();

  const animation =
    props.nested && !sidebarTransitionEnded
      ? animateNavigationMenu(
          getNavigationMenuHeight(props.itemCount, theme),
          sidebarCollapsed,
        )
      : undefined;

  const renderSidebarCollapsed = getRenderSidebarCollapsed(
    sidebarCollapsed,
    sidebarTransitionEnded,
  );

  return (
    <NavigationMenuList
      data-testid="menu.navigation"
      nested={Boolean(props.nested)}
      collapsed={renderSidebarCollapsed}
      $animation={animation}
    >
      {props.children}
    </NavigationMenuList>
  );
}
